import { apiClient } from './api';
import { CommonState } from '@erpgold/shared';

export interface Page {
  page_id: string;
  title: string;
  sapo: string;
  slug: string;
  alias: string;
  thumbnail: string;
  thumbnail_mobile: string;
  description: string;
  tags: string[];
  metadata: Record<string, any>;
  author: string;
  state?: CommonState;
  created_at: string;
  updated_at: string;
}

export interface Article {
  article_id: string;
  title: string;
  sapo: string;
  slug: string;
  alias: string;
  thumbnail: string;
  description: string;
  tags: string[];
  metadata: Record<string, any>;
  category: any;
  author: string;
  state?: CommonState;
  created_at: string;
  updated_at: string;
}

export interface ArticleCategory {
  category_id: string;
  _id: string;
  name: string;
  parent_id: string | null;
  description: string;
  image_url: string;
  ordering: number;
  state: CommonState;
  name_alias: string;
  slug: string;
  metadata: Record<string, any>;
  article_count: number;
  level?: number;
  children?: ArticleCategory[];
}

export interface CreatePageDto {
  title: string;
  tenant_id: string;
  sapo?: string;
  slug?: string;
  thumbnail?: string;
  thumbnail_mobile?: string;
  description: string;
  tags?: string[];
  metadata?: Record<string, any>;
  state?: CommonState;
}

export interface UpdatePageDto {
  title?: string;
  sapo?: string;
  slug?: string;
  thumbnail?: string;
  thumbnail_mobile?: string;
  description?: string;
  tags?: string[];
  metadata?: Record<string, any>;
  state?: CommonState;
}

export interface PageQueryDto {
  page?: number;
  limit?: number;
  q?: string;
  state?: number;
  created_at?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface CreateArticleDto {
  title: string;
  tenant_id: string;
  sapo?: string;
  slug?: string;
  category_id: string;
  thumbnail?: string;
  description: string;
  tags?: string[];
  metadata?: Record<string, any>;
  state?: CommonState;
}

export interface UpdateArticleDto {
  title?: string;
  sapo?: string;
  slug?: string;
  category_id?: string;
  thumbnail?: string;
  description?: string;
  tags?: string[];
  metadata?: Record<string, any>;
  state?: CommonState;
}

export interface ArticleQueryDto {
  page?: number;
  limit?: number;
  q?: string;
  state?: number;
  created_at?: string;
  category_id?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface CreateArticleCategoryDto {
  name: string;
  tenant_id: string;
  parent_id?: string;
  description?: string;
  image_url?: string;
  ordering?: number;
  state?: CommonState;
  metadata?: {
    seo_title?: string;
    seo_description?: string;
    seo_keywords?: string;
  };
}

export interface UpdateArticleCategoryDto {
  name?: string;
  parent_id?: string;
  description?: string;
  image_url?: string;
  ordering?: number;
  state?: CommonState;
  metadata?: {
    seo_title?: string;
    seo_description?: string;
    seo_keywords?: string;
  };
}

export interface ContentResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const contentService = {
  pages: {
    getAll: async (filters: PageQueryDto = {}): Promise<ContentResponse<Page>> => {
      const response = await apiClient.get('/content/pages', { params: filters });
      return response.data.data;
    },
    getById: async (id: string): Promise<Page> => {
      const response = await apiClient.get(`/content/pages/${id}`);
      return response.data.data;
    },
    create: async (data: CreatePageDto): Promise<Page> => {
      const response = await apiClient.post('/content/pages', data);
      return response.data.data;
    },
    update: async (id: string, data: UpdatePageDto): Promise<Page> => {
      const response = await apiClient.put(`/content/pages/${id}`, data);
      return response.data.data;
    },
    toggleState: async (id: string): Promise<Page> => {
      const response = await apiClient.patch(`/content/pages/${id}/toggle-state`);
      return response.data.data;
    },
    deleteMany: async (ids: string[]): Promise<void> => {
      await apiClient.delete('/content/pages', { data: { ids } });
    },
    getForSelect: async (query: { limit?: number; search?: string; state?: number } = {}): Promise<{ pages: Page[]; total: number }> => {
      const response = await apiClient.get('/content/pages-list', { params: query });
      return response.data.data;
    },
  },

  articles: {
    getAll: async (filters: ArticleQueryDto = {}): Promise<ContentResponse<Article>> => {
      const response = await apiClient.get('/content/articles', { params: filters });
      return response.data.data;
    },
    getById: async (id: string): Promise<Article> => {
      const response = await apiClient.get(`/content/articles/${id}`);
      return response.data.data;
    },
    create: async (data: CreateArticleDto): Promise<Article> => {
      const response = await apiClient.post('/content/articles', data);
      return response.data.data;
    },
    update: async (id: string, data: UpdateArticleDto): Promise<Article> => {
      const response = await apiClient.put(`/content/articles/${id}`, data);
      return response.data.data;
    },
    toggleState: async (id: string): Promise<Article> => {
      const response = await apiClient.patch(`/content/articles/${id}/toggle-state`);
      return response.data.data;
    },
    deleteMany: async (ids: string[]): Promise<void> => {
      await apiClient.delete('/content/articles', { data: { ids } });
    },
    getForSelect: async (query: { limit?: number; search?: string; category_id?: string } = {}): Promise<{ articles: Article[]; total: number }> => {
      const response = await apiClient.get('/content/articles-list', { params: query });
      return response.data.data;
    },
  },

  articleCategories: {
    getAll: async (query: { q?: string; state?: number; tenant_id?: string; sort?: string; order?: string } = {}): Promise<ArticleCategory[]> => {
      const response = await apiClient.get('/content/article-categories', { params: query });
      return response.data.data;
    },
    create: async (data: CreateArticleCategoryDto): Promise<ArticleCategory> => {
      const response = await apiClient.post('/content/article-categories', data);
      return response.data.data;
    },
    update: async (id: string, data: UpdateArticleCategoryDto): Promise<ArticleCategory> => {
      const response = await apiClient.put(`/content/article-categories/${id}`, data);
      return response.data.data;
    },
    deleteMany: async (ids: string[]): Promise<void> => {
      await apiClient.delete('/content/article-categories', { data: { ids } });
    },
    updateOrdering: async (categories: Array<{ _id: string; ordering: number }>): Promise<void> => {
      await apiClient.put('/content/article-categories/ordering', { categories });
    },
    getForSelect: async (query: { search?: string } = {}): Promise<ArticleCategory[]> => {
      const response = await apiClient.get('/content/article-categories-list', { params: query });
      return response.data.data;
    },
  },
};

export default contentService;